import React from 'react';
import { Quote } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { openBookDemo } from '../utils/config';

const Testimonials = () => {
  const { t } = useTranslation();

  const testimonials = [
    t('testimonials.items.testimonial1', { returnObjects: true }),
    t('testimonials.items.testimonial2', { returnObjects: true }),
    t('testimonials.items.testimonial3', { returnObjects: true }),
    t('testimonials.items.testimonial4', { returnObjects: true }),
    t('testimonials.items.testimonial5', { returnObjects: true }),
    t('testimonials.items.testimonial6', { returnObjects: true })
  ];

  return (
    <section id="testimonials" className="py-24 bg-gray-50 relative overflow-hidden">
      {/* Background Decorations */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-blue-50 rounded-full filter blur-3xl opacity-40"></div>
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-blue-100 rounded-full filter blur-3xl opacity-40"></div>
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Header */}
        <div className="text-center max-w-3xl mx-auto mb-16">
          <p className="text-sm text-blue-600 uppercase tracking-wider mb-4 font-semibold">{t('testimonials.title')}</p>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            {t('testimonials.title')}
          </h2>
          <p className="text-xl text-gray-600">
            {t('testimonials.subtitle')}
          </p>
        </div>
        
        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {testimonials.map((testimonial) => {
            // Different colors for corporate vs individual testimonials
            const isIndividual = testimonial.type === 'individual';
            const borderColor = isIndividual ? 'border-emerald-300' : 'border-blue-300';
            const badgeBg = isIndividual ? 'bg-emerald-50 border-emerald-200 text-emerald-700' : 'bg-blue-50 border-blue-200 text-blue-700';
            const quoteIconBg = isIndividual ? 'bg-emerald-50' : 'bg-blue-50';
            const quoteIconColor = isIndividual ? 'text-emerald-600' : 'text-blue-600';
            
            return (
              <div 
                key={testimonial.author} 
                className={`relative p-8 bg-white rounded-2xl border-2 ${borderColor} hover:shadow-xl transition-all duration-300 group hover:-translate-y-2`}
              >
                {/* Type Badge */}
                {isIndividual && (
                  <div className="absolute -top-3 right-8 px-3 py-1 bg-emerald-500 text-white text-xs font-medium rounded-full">
                    Individual
                  </div>
                )}
                
                {/* Quote Icon */}
                <div className={`absolute -top-4 left-8 w-12 h-12 rounded-full ${quoteIconBg} border-2 ${borderColor} flex items-center justify-center shadow-md`}>
                  <Quote className={`w-6 h-6 ${quoteIconColor}`} />
                </div>
                
                {/* Quote */}
                <p className="text-gray-700 leading-relaxed mb-6 mt-6 italic">
                  "{testimonial.quote}"
                </p>
                
                {/* Results Badge */}
                <div className={`inline-block px-4 py-2 ${badgeBg} border rounded-full text-sm mb-6 font-medium`}>
                  {testimonial.results}
                </div>
                
                {/* Author */}
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-full ${isIndividual ? 'bg-emerald-100' : 'bg-blue-100'} flex items-center justify-center text-lg font-bold ${isIndividual ? 'text-emerald-700' : 'text-blue-700'}`}>
                    {testimonial.author.split(' ').map(n => n[0]).join('')}
                  </div>
                  <div>
                    <div className="font-semibold text-gray-900">{testimonial.author}</div>
                    <div className="text-sm text-gray-600">{testimonial.role}</div>
                    <div className="text-xs text-gray-500">{testimonial.company}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        
        {/* Bottom CTA */}
        <div className="text-center mt-16">
          <button 
            onClick={openBookDemo}
            className="inline-flex items-center px-8 py-4 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-300"
          >
            {t('nav.bookDemo')}
          </button>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
